import React, {Fragment, ReactNode, useEffect, useState} from "react";
import has from "lodash/has";
import get from "lodash/get";
import FormInputStyleProperties from "@app/component/form/input/FormInputStyleProperties";
import {useTranslation} from "@app/component/translation/Translation";
import Utilities from "@util/Utilities";
import _isEmpty from "lodash/isEmpty";
import _isArray from "lodash/isArray";
import { Label, Listbox, ListboxButton, ListboxOptions, Transition } from '@headlessui/react';
import {ChevronUpDownIcon} from "@heroicons/react/20/solid";
import {useOutsideClick} from "@app/component/outside-click/HandleOutsideClickHook";

type Props = {
    isRequired?: boolean,
    placeholder?: string,
    form?: any,
    name: string,
    label?: string,
    labelHint?: React.JSX.Element,
    disabled?: boolean,
    className?: string,
    extraClassName?: string,
    hideErrorMessage?: boolean,
    defaultValue?: any[],
    options?: Option[],
    hideAllSelection?: boolean,
    maxNumberOfSelections?: number,
    onChange?: (val: any) => any
} & FormInputStyleProperties;

type Option = {
    id: string,
    label: string | ReactNode,
    value: any,
    extraInfo?: string,
    iconArea?: ReactNode
}

const FormMultiSelect: React.FunctionComponent<Props> = (
    {
        form,
        placeholder,
        name,
        isRequired = false,
        label,
        isLoading,
        disabled = false,
        className,
        extraClassName,
        labelHint,
        hideErrorMessage = false,
        defaultValue = [],
        options,
        hideAllSelection,
        maxNumberOfSelections,
        onChange
    }) => {

    const {t} = useTranslation("common.form.error");
    const hasError = !!form && has(form.formState.errors, name);
    const errorMessage = hasError ? (get(form.formState.errors, name + ".message") || t("required")) : '';
    const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const ref = useOutsideClick(() => setIsOpen(false));
    const [isAll, setIsAll] = useState<boolean>();

    const val = form.watch(name);

    useEffect(() => {
        const selected = findOptions(val);
        setIsAll(selected.length === options!.length)
        setSelectedOptions(selected)
        if(!!onChange) onChange(val)
    }, [val]);


    const findOptions = (values: any[]): Option[] | [] => {
        if(!!values && !_isArray(values)) return [options!.find(o => o.value == values)!]
        const opts: Option[] = [];
        if (!_isEmpty(values)) {
            values.forEach(v => {
                const option = options!.find(o => o.value == v);
                if(!!option) opts.push(option)
            })
        }
        return opts;
    }

    const checkIsAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) form.setValue(name, options!.map(o => ''+o.value))
        else form.setValue(name, [])
    }

    const displayValue = () => {
        return selectedOptions.length < 4 ? selectedOptions.filter(o=>!!o).map(o => o.label).join(", ") : selectedOptions.length + " selections";
    }

    const isSelected = (optionValue: string) => {
        return selectedOptions.findIndex(s=>!!s && s.value === optionValue) > -1
    }

    const renderField = () => {
        return <>
            {!!form && <Listbox disabled={disabled} as="div" ref={ref}>
                {() => (
                    <>

                        <div className="flex justify-between">
                            {!!label && <Label className="block text-sm font-medium leading-6 text-gray-900">
                                {label}
                            </Label>}
                            {!!labelHint ? <span className="text-xs leading-6 text-gray-500">{labelHint}</span> : ''}
                        </div>

                        <div className="relative mt-0.5">
                            <ListboxButton onClick={() => setIsOpen(!isOpen)}
                                            className={Utilities.classNames(
                                                !!className ? className : "input relative cursor-default pl-3 bg-white",
                                                Utilities.NVL(hasError, 'input-error'),
                                                Utilities.NVL(isLoading, 'input-isLoading'),
                                                Utilities.NVL(disabled, 'input-disabled'),
                                                'focus:outline-none',
                                                _isEmpty(selectedOptions) ? "" : "bg-white",
                                                extraClassName)
                                            }>
                                <span className="inline-flex w-full truncate">
                                    <span
                                        className={Utilities.classNames(_isEmpty(selectedOptions) && !!placeholder ? "text-slate-400" : "bg-white", "truncate max-w-md")}>{_isEmpty(selectedOptions) ? placeholder : displayValue()}</span>
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                </span>
                            </ListboxButton>

                            <Transition
                                show={isOpen}
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <ListboxOptions style={{zIndex: "1000"}}
                                                 className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none min-w-[100px] divide-y divide-gray-100">
                                    {!hideAllSelection && <>
                                        <div className="py-1" role="none">
                                            <div
                                                className="text-gray-700 group flex gap-x-2 items-center px-4 py-2 text-sm"
                                                role="menuitem">
                                                <input id="ALL" type="checkbox" value="ALL" checked={isAll}
                                                       onChange={checkIsAll}
                                                       className="input-checkbox"/>
                                                <label htmlFor="ALL">All</label>
                                            </div>
                                        </div>
                                    </>}
                                    <div>
                                        {renderOptions()}
                                    </div>
                                </ListboxOptions>
                            </Transition>
                        </div>
                    </>
                )}
            </Listbox>}
        </>
    }

    const renderOption = (o: Option) => {
        const isDisabled = maxNumberOfSelections === selectedOptions?.length && !isSelected(o.value);
        return <div key={o.id} className="text-gray-700 group flex gap-x-2 items-center px-4 py-2 text-sm"
                    id="headlessui-menu-item-:r4b:" role="menuitem">
            <input type="checkbox" className={isDisabled?"input-checkbox-disabled": "input-checkbox"} id={`id_${o.value}`} checked={isSelected(o.value)} disabled={isDisabled}
                   value={o.value} {...form.register(name, {validate: (value: string[]) => !isRequired || !_isEmpty(value)})}/><label className={isDisabled?"label-disabled": "label"} htmlFor={`id_${o.value}`}>{o.label}</label>
        </div>
    }

    const renderOptions = () => {
        return <>
            {!_isEmpty(options) ? options!.map(o => renderOption(o)) : ''}
        </>
    }

    return <>
        {renderField()}
        {!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
    </>

}

export default FormMultiSelect;